<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { z } from "zod";

import FavIcon from "@/components/FavIcon.vue";
import OrganisationSelectorModal from "@/components/OrganisationSelectorModal.vue";
import OrganisationsMap from "@/components/OrganisationsMap.vue";
import OrganisationsMapFilter, { type FilterCriteria } from "@/components/OrganisationsMapFilter.vue";
import OrganisationStatisticsModal from "@/components/OrganisationStatisticsModal.vue";
import OrganisationTypeIcon from "@/components/OrganisationTypeIcon.vue";
import SidebarFilter from "@/components/SidebarFilter.vue";
import SidebarFilterButton from "@/components/SidebarFilterButton.vue";
import { useValidatedProps } from "@/composable";
import {
  type Organisation,
  organisationTypes,
  organisationWithQuestionnaireIdsSchema,
  questionnaireSchema
} from "@/types";

const { questionnaires, organisations } = toRefs(
  useValidatedProps(
    z.object({
      questionnaires: z.array(questionnaireSchema),
      organisations: z.array(organisationWithQuestionnaireIdsSchema)
    })
  )
);

const filterCriteria = ref<FilterCriteria>({
  organisationType: undefined,
  questionnaireId: undefined
});

const filteredOrganisations = computed(() => {
  const { organisationType, questionnaireId } = filterCriteria.value;

  return organisations.value.filter((organisation) => {
    if (organisationType !== undefined && organisation.type !== organisationType) {
      return false;
    }

    if (questionnaireId !== undefined && !organisation.questionnaireIds.includes(questionnaireId)) {
      return false;
    }

    return true;
  });
});

const selectedOrganisation = ref<Organisation | Organisation[]>();
</script>

<template>
  <div class="d-flex dvh-100">
    <main class="flex-grow-1">
      <SidebarFilterButton class="position-absolute top-0 end-0 z-1 m-2" />

      <OrganisationsMap
        :organisations="filteredOrganisations"
        class="w-100 h-100"
        @select="(organisation) => (selectedOrganisation = organisation)"
      />
      <OrganisationSelectorModal
        v-if="Array.isArray(selectedOrganisation)"
        :organisations="selectedOrganisation"
        @did-hide="selectedOrganisation = undefined"
        @select="(organisation) => (selectedOrganisation = organisation)"
      />
      <OrganisationStatisticsModal
        v-else-if="selectedOrganisation"
        :organisation="selectedOrganisation"
        @did-hide="selectedOrganisation = undefined"
      />
    </main>
    <SidebarFilter>
      <template #title><FavIcon /> PRO-landkort</template>
      <p>
        PRO-landkort viser anvendelsen af PRO spørgeskemaer over hele landets kommuner og hospitaler. Når man klikker på
        <span class="text-nowrap">
          <OrganisationTypeIcon v-for="type in organisationTypes" :key="type" class="flex-shrink-0" :type />
        </span>
        kan man se grafer for udviklingen af anvendelsen af PRO spørgeskemaer.
      </p>
      <OrganisationsMapFilter v-model="filterCriteria" :questionnaires="questionnaires" />
    </SidebarFilter>
  </div>
</template>
